import { DOMAIN_FAMILIES } from './families-domains.js';
import { EXTRA_FAMILIES } from './families-extra.js';
/**
 * Familles d'ouvrages : DÉFINITIONS sous forme de données (paramètres + formules DSL).
 * Les familles système ci-dessous sont installées en base ; une entreprise peut en créer
 * d'autres depuis l'interface sans modification du code (règle absolue du cahier des charges).
 *
 * Variables réservées, injectées par le moteur dans chaque calcul :
 *   n      nombre d'éléments (U) — quantité de l'instance (« S1 × 8 ») ou 1 en mode détaillé
 *   S_ouv  surface totale des ouvertures déduites (m²) — familles qui acceptent des ouvertures
 */

export type FamilyGroup = 'FOUNDATION' | 'VERTICAL' | 'HORIZONTAL' | 'MASONRY' | 'FINISH' | 'ROOF' | 'EARTHWORK' | 'OTHER';

export interface FamilyParameter {
  key: string;
  label: string;
  /** Code d'unité (m, cm, m2, U…) ; vide = sans dimension. */
  unit: string;
  /** Valeur par défaut de la famille (chaîne décimale) ; absente = à renseigner. */
  default?: string;
  min?: string;
  max?: string;
  /** Si non renseigné ailleurs, prend la valeur d'une propriété du niveau. */
  fromLevel?: 'height';
  /** Paramètre décrivant la section (affiché dans la nomenclature). */
  section?: boolean;
  /** Valeur entière (nombre de marches…). */
  integer?: boolean;
}

/** Nature d'une quantité : sert aux totaux par niveau, au remblai et au quantitatif. */
/** VOLUME : volume d'ouvrage autre que du béton (hérissonnage…) ; COUNT : nombre d'unités (portes, appareils…). */
export type OutputKind = 'CONCRETE' | 'FORMWORK' | 'BLINDING' | 'EXCAVATION' | 'BACKFILL' | 'MASONRY' | 'MASONRY_VOLUME' | 'AREA' | 'LENGTH' | 'VOLUME' | 'COUNT' | 'OTHER';
export const OUTPUT_KINDS: readonly OutputKind[] = ['CONCRETE', 'FORMWORK', 'BLINDING', 'EXCAVATION', 'BACKFILL', 'MASONRY', 'MASONRY_VOLUME', 'AREA', 'LENGTH', 'VOLUME', 'COUNT', 'OTHER'];

export interface FamilyOutput {
  key: string;
  label: string;
  unit: string;
  formula: string;
  /** Sortie principale (quantité de référence de l'élément). */
  primary?: boolean;
  /** Nature de la quantité, pour les agrégations par niveau (béton, coffrage…). */
  kind?: OutputKind;
}

export interface FamilyDefinition {
  code: string;
  name: string;
  group: FamilyGroup;
  description?: string;
  parameters: FamilyParameter[];
  outputs: FamilyOutput[];
  /** Découpage en tronçons (section variable le long de l'élément). */
  segmentable?: boolean;
  /** Paramètre « longueur » des tronçons (L par défaut). */
  segmentLengthKey?: string;
  /** Accepte des ouvertures déduites (S_ouv). */
  openings?: boolean;
  /**
   * Ouvrage enterré lorsqu'il est placé sur un niveau Fondations / Sous-sol : son béton, son béton
   * de propreté et son volume de maçonnerie sont déduits des fouilles pour calculer le remblai.
   */
  buried?: boolean;
  /** Lot du quantitatif (code) ; à défaut, déduit du groupe de la famille. */
  chapter?: string;
  /** Une ligne de quantitatif par type (enduit tyrolien ≠ enduit lisse, porte ≠ fenêtre…). */
  lineByType?: boolean;
  /** Ressources par défaut des types de la famille (mortier, compositions…), modifiables par type. */
  defaults?: { concrete?: string; blinding?: string; masonry?: string; mortar?: string; kits?: string[] };
}

const m = (key: string, label: string, extra: Partial<FamilyParameter> = {}): FamilyParameter => ({ key, label, unit: 'm', ...extra });

export const RESERVED_VARIABLES = ['n', 'S_ouv'] as const;

const BASE_FAMILIES: readonly FamilyDefinition[] = [
  {
    code: 'SEMELLE_ISOLEE',
    buried: true,
    name: 'Semelle isolée',
    group: 'FOUNDATION',
    parameters: [
      m('a', 'Longueur', { section: true }),
      m('b', 'Largeur', { section: true }),
      m('h', 'Hauteur (épaisseur)', { section: true }),
      m('d', 'Débord du béton de propreté', { default: '0.05' }),
      m('e_bp', 'Épaisseur du béton de propreté', { default: '0.05' }),
      m('s', 'Surlargeur de fouille (par côté)', { default: '0.20' }),
      m('p', 'Profondeur de fouille'),
    ],
    outputs: [
      { key: 'volume', label: 'Béton', unit: 'm3', formula: 'a * b * h * n', primary: true, kind: 'CONCRETE' },
      { key: 'coffrage', label: 'Coffrage', unit: 'm2', formula: '2 * (a + b) * h * n', kind: 'FORMWORK' },
      { key: 'proprete', label: 'Béton de propreté', unit: 'm3', formula: '(a + 2 * d) * (b + 2 * d) * e_bp * n', kind: 'BLINDING' },
      { key: 'fouille', label: 'Fouille en puits', unit: 'm3', formula: '(a + 2 * s) * (b + 2 * s) * p * n', kind: 'EXCAVATION' },
    ],
  },
  {
    code: 'SEMELLE_FILANTE',
    buried: true,
    name: 'Semelle filante',
    group: 'FOUNDATION',
    segmentable: true,
    parameters: [m('L', 'Longueur'), m('b', 'Largeur', { section: true }), m('h', 'Hauteur', { section: true }), m('d', 'Débord du béton de propreté', { default: '0.05' }), m('e_bp', 'Épaisseur du béton de propreté', { default: '0.05' })],
    outputs: [
      { key: 'volume', label: 'Béton', unit: 'm3', formula: 'L * b * h * n', primary: true, kind: 'CONCRETE' },
      { key: 'coffrage', label: 'Coffrage', unit: 'm2', formula: '2 * L * h * n', kind: 'FORMWORK' },
      { key: 'proprete', label: 'Béton de propreté', unit: 'm3', formula: 'L * (b + 2 * d) * e_bp * n', kind: 'BLINDING' },
    ],
  },
  {
    code: 'RADIER',
    buried: true,
    name: 'Radier BA',
    group: 'FOUNDATION',
    parameters: [m('Lx', 'Longueur', { section: true }), m('Ly', 'Largeur', { section: true }), m('e', 'Épaisseur', { section: true }), m('d', 'Débord du béton de propreté', { default: '0.10' }), m('e_bp', 'Épaisseur du béton de propreté', { default: '0.05' })],
    outputs: [
      { key: 'volume', label: 'Béton', unit: 'm3', formula: 'Lx * Ly * e * n', primary: true, kind: 'CONCRETE' },
      { key: 'coffrage', label: 'Coffrage périphérique', unit: 'm2', formula: '2 * (Lx + Ly) * e * n', kind: 'FORMWORK' },
      { key: 'proprete', label: 'Béton de propreté', unit: 'm3', formula: '(Lx + 2 * d) * (Ly + 2 * d) * e_bp * n', kind: 'BLINDING' },
    ],
  },
  {
    code: 'LONGRINE',
    buried: true,
    name: 'Longrine',
    group: 'FOUNDATION',
    segmentable: true,
    parameters: [m('L', 'Longueur'), m('b', 'Largeur', { section: true }), m('h', 'Hauteur', { section: true })],
    outputs: [
      { key: 'volume', label: 'Béton', unit: 'm3', formula: 'L * b * h * n', primary: true, kind: 'CONCRETE' },
      { key: 'coffrage', label: 'Coffrage (2 joues)', unit: 'm2', formula: '2 * h * L * n', kind: 'FORMWORK' },
      { key: 'longueur', label: 'Longueur totale', unit: 'm', formula: 'L * n', kind: 'LENGTH' },
    ],
  },
  {
    code: 'POTEAU',
    buried: true,
    name: 'Poteau BA',
    group: 'VERTICAL',
    parameters: [m('a', 'Côté a', { section: true }), m('b', 'Côté b', { section: true }), m('H', 'Hauteur', { fromLevel: 'height' })],
    outputs: [
      { key: 'volume', label: 'Béton', unit: 'm3', formula: 'a * b * H * n', primary: true, kind: 'CONCRETE' },
      { key: 'coffrage', label: 'Coffrage', unit: 'm2', formula: '2 * (a + b) * H * n', kind: 'FORMWORK' },
    ],
  },
  {
    code: 'VOILE',
    buried: true,
    name: 'Voile BA',
    group: 'VERTICAL',
    openings: true,
    parameters: [m('L', 'Longueur'), m('H', 'Hauteur', { fromLevel: 'height' }), m('e', 'Épaisseur', { section: true })],
    outputs: [
      { key: 'volume', label: 'Béton (net)', unit: 'm3', formula: '(L * H * n - S_ouv) * e', primary: true, kind: 'CONCRETE' },
      { key: 'volume_brut', label: 'Béton (brut)', unit: 'm3', formula: 'L * H * e * n', kind: 'OTHER' },
      { key: 'coffrage', label: 'Coffrage (2 faces)', unit: 'm2', formula: '2 * (L * H * n - S_ouv)', kind: 'FORMWORK' },
    ],
  },
  {
    code: 'POUTRE',
    name: 'Poutre BA',
    group: 'HORIZONTAL',
    segmentable: true,
    parameters: [m('L', 'Longueur'), m('b', 'Largeur', { section: true }), m('h', 'Hauteur', { section: true })],
    outputs: [
      { key: 'volume', label: 'Béton', unit: 'm3', formula: 'L * b * h * n', primary: true, kind: 'CONCRETE' },
      { key: 'coffrage', label: 'Coffrage (fond + 2 joues)', unit: 'm2', formula: '(b + 2 * h) * L * n', kind: 'FORMWORK' },
      { key: 'longueur', label: 'Longueur totale', unit: 'm', formula: 'L * n', kind: 'LENGTH' },
    ],
  },
  {
    code: 'CHAINAGE',
    name: 'Chaînage / linteau BA',
    group: 'HORIZONTAL',
    segmentable: true,
    parameters: [m('L', 'Longueur'), m('b', 'Largeur', { section: true }), m('h', 'Hauteur', { section: true })],
    outputs: [
      { key: 'volume', label: 'Béton', unit: 'm3', formula: 'L * b * h * n', primary: true, kind: 'CONCRETE' },
      { key: 'coffrage', label: 'Coffrage (2 joues)', unit: 'm2', formula: '2 * h * L * n', kind: 'FORMWORK' },
    ],
  },
  {
    code: 'DALLE_PLEINE',
    name: 'Dalle pleine',
    group: 'HORIZONTAL',
    openings: true,
    parameters: [m('Lx', 'Longueur'), m('Ly', 'Largeur'), m('e', 'Épaisseur', { section: true })],
    outputs: [
      { key: 'volume', label: 'Béton', unit: 'm3', formula: '(Lx * Ly * n - S_ouv) * e', primary: true, kind: 'CONCRETE' },
      { key: 'coffrage', label: 'Coffrage (sous-face)', unit: 'm2', formula: 'Lx * Ly * n - S_ouv', kind: 'FORMWORK' },
      { key: 'surface', label: 'Surface', unit: 'm2', formula: 'Lx * Ly * n - S_ouv', kind: 'AREA' },
    ],
  },
  {
    code: 'PLANCHER_HOURDIS',
    name: 'Plancher à hourdis (corps creux)',
    group: 'HORIZONTAL',
    openings: true,
    parameters: [
      { key: 'S', label: 'Surface du plancher', unit: 'm2' },
      m('e_comp', 'Épaisseur de la dalle de compression', { default: '0.05', section: true }),
    ],
    outputs: [
      { key: 'surface', label: 'Surface', unit: 'm2', formula: 'S * n - S_ouv', primary: true, kind: 'AREA' },
      { key: 'compression', label: 'Béton de la dalle de compression', unit: 'm3', formula: '(S * n - S_ouv) * e_comp', kind: 'CONCRETE' },
    ],
  },
  {
    code: 'DALLAGE',
    name: 'Dallage sur terre-plein',
    group: 'HORIZONTAL',
    parameters: [
      { key: 'S', label: 'Surface', unit: 'm2' },
      m('e', 'Épaisseur', { section: true }),
      m('e_remblai', 'Épaisseur du remblai sableux', { default: '0.20' }),
    ],
    outputs: [
      { key: 'volume', label: 'Béton', unit: 'm3', formula: 'S * e * n', primary: true, kind: 'CONCRETE' },
      { key: 'surface', label: 'Surface (polyane, chape)', unit: 'm2', formula: 'S * n', kind: 'AREA' },
      { key: 'remblai', label: 'Remblai sous dallage', unit: 'm3', formula: 'S * e_remblai * n', kind: 'BACKFILL' },
    ],
  },
  {
    code: 'ESCALIER',
    name: 'Escalier BA (paillasse + marches)',
    group: 'VERTICAL',
    parameters: [
      { key: 'nb', label: 'Nombre de marches', unit: '', integer: true },
      m('g', 'Giron', { default: '0.28' }),
      m('hm', 'Hauteur de marche', { default: '0.17' }),
      m('l', 'Emmarchement (largeur)', { section: true }),
      m('ep', 'Épaisseur de la paillasse', { default: '0.15', section: true }),
    ],
    outputs: [
      { key: 'volume', label: 'Béton', unit: 'm3', formula: 'l * (ep * sqrt((nb * g) ^ 2 + (nb * hm) ^ 2) + nb * g * hm / 2) * n', primary: true, kind: 'CONCRETE' },
      { key: 'coffrage', label: 'Coffrage (sous-face)', unit: 'm2', formula: 'l * sqrt((nb * g) ^ 2 + (nb * hm) ^ 2) * n', kind: 'FORMWORK' },
    ],
  },
  {
    code: 'ACROTERE',
    name: 'Acrotère BA',
    group: 'ROOF',
    parameters: [m('L', 'Longueur'), m('h', 'Hauteur', { section: true }), m('e', 'Épaisseur', { section: true })],
    outputs: [
      { key: 'volume', label: 'Béton', unit: 'm3', formula: 'L * h * e * n', primary: true, kind: 'CONCRETE' },
      { key: 'coffrage', label: 'Coffrage (2 faces)', unit: 'm2', formula: '2 * L * h * n', kind: 'FORMWORK' },
    ],
  },
  {
    code: 'MUR_MACONNERIE',
    buried: true,
    name: 'Mur en maçonnerie',
    group: 'MASONRY',
    openings: true,
    parameters: [m('L', 'Longueur'), m('H', 'Hauteur', { fromLevel: 'height' }), m('e', 'Épaisseur du bloc', { section: true })],
    outputs: [
      { key: 'surface', label: 'Surface nette', unit: 'm2', formula: 'L * H * n - S_ouv', primary: true, kind: 'MASONRY' },
      { key: 'surface_brute', label: 'Surface brute', unit: 'm2', formula: 'L * H * n', kind: 'OTHER' },
      { key: 'volume_maconnerie', label: 'Volume de maçonnerie', unit: 'm3', formula: '(L * H * n - S_ouv) * e', kind: 'MASONRY_VOLUME' },
    ],
  },
  {
    code: 'FOUILLE_RIGOLE',
    name: 'Fouille en rigole',
    group: 'EARTHWORK',
    segmentable: true,
    parameters: [m('L', 'Longueur'), m('b', 'Largeur', { section: true }), m('p', 'Profondeur', { section: true })],
    outputs: [{ key: 'volume', label: 'Déblai', unit: 'm3', formula: 'L * b * p * n', primary: true, kind: 'EXCAVATION' }],
  },
  {
    code: 'ENDUIT_PEINTURE',
    name: 'Enduit / peinture sur murs',
    group: 'FINISH',
    openings: true,
    parameters: [m('L', 'Longueur développée'), m('H', 'Hauteur', { fromLevel: 'height' }), { key: 'faces', label: 'Nombre de faces', unit: '', default: '2', integer: true }],
    outputs: [{ key: 'surface', label: 'Surface à traiter', unit: 'm2', formula: '(L * H * n - S_ouv) * faces', primary: true, kind: 'AREA' }],
  },
  {
    code: 'REVETEMENT_SOL',
    name: 'Revêtement de sol (carrelage, chape)',
    group: 'FINISH',
    parameters: [{ key: 'S', label: 'Surface', unit: 'm2' }, m('P', 'Périmètre (plinthes)', { default: '0' })],
    outputs: [
      { key: 'surface', label: 'Surface', unit: 'm2', formula: 'S * n', primary: true, kind: 'AREA' },
      { key: 'plinthe', label: 'Plinthes', unit: 'ml', formula: 'P * n', kind: 'LENGTH' },
    ],
  },
  {
    code: 'COUVERTURE',
    name: 'Couverture (tôle bac, tuiles)',
    group: 'ROOF',
    parameters: [m('Lx', 'Longueur en plan'), m('Ly', 'Largeur en plan'), m('debord', 'Débord de toiture (par côté)', { default: '0.50' }), { key: 'k_pente', label: 'Coefficient de pente (1/cos α)', unit: '', default: '1.05' }],
    outputs: [{ key: 'surface', label: 'Surface couverte (rampant)', unit: 'm2', formula: '(Lx + 2 * debord) * (Ly + 2 * debord) * k_pente * n', primary: true, kind: 'AREA' }],
  },
];

/** Familles ExpertDevis BTP : gros œuvre d'origine, puis familles complémentaires (second œuvre, réseaux, assainissement). */
export const SYSTEM_FAMILIES: readonly FamilyDefinition[] = [...BASE_FAMILIES, ...EXTRA_FAMILIES, ...DOMAIN_FAMILIES];

export function findSystemFamily(code: string): FamilyDefinition | undefined {
  return SYSTEM_FAMILIES.find((f) => f.code === code);
}
